import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { BarChart3, TrendingUp, TrendingDown, Shield, Zap, Clock, ChevronRight, FileVideo, Image, Music } from "lucide-react";
import { LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_API_URL}/api`;

const VERDICT_COLORS = {
  FAKE: "hsl(0, 72%, 51%)",
  REAL: "hsl(142, 71%, 45%)",
  UNCERTAIN: "hsl(45, 93%, 47%)",
};

const MEDIA_COLORS = {
  upload: "#14B8A6",
  url: "#3b82f6",
};

function StatCard({ label, value, sub, icon: Icon, trend, delay = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      className="rounded-md border border-border bg-card p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="text-3xl font-bold font-heading tracking-tight">{value}</div>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      {trend !== undefined && trend !== 0 && (
        <div className={`flex items-center gap-1 mt-2 text-xs font-mono ${trend > 0 ? "text-verdict-real" : "text-red-400"}`}>
          {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {trend > 0 ? "+" : ""}{trend} vs minggu lalu
        </div>
      )}
    </motion.div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-sm border border-border bg-background p-2.5 shadow-lg text-xs">
      <p className="font-mono text-muted-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-semibold" style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
}

export default function UserAnalytics() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/analytics/me`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.detail || err.message || "Unknown error"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <main className="container mx-auto max-w-6xl p-6">
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="container mx-auto max-w-6xl p-6">
        <p className="text-center text-muted-foreground py-20">{error || t("common.error")}</p>
      </main>
    );
  }

  const pieData = Object.entries(data.verdict_distribution || {}).map(([name, value]) => ({
    name, value, color: VERDICT_COLORS[name] || "#888",
  }));

  const weeklyData = (data.weekly_trend || [])
    .filter(w => w.week)
    .map(w => ({
      ...w,
      label: `W${w.week.split("-")[1]}`,
    }));

  const PLAN_LABELS = { free: "Free", starter: "Starter", pro: "Pro", business: "Business" };
  const usagePct = Math.min(100, Math.round((data.usage_count / Math.max(data.usage_limit, 1)) * 100));

  return (
    <main className="container mx-auto max-w-7xl p-6 md:p-8 md:" data-testid="user-analytics-page">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <h1 className="font-heading text-2xl font-bold tracking-tight">{t("analytics.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("analytics.subtitle")}</p>
      </motion.div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label={t("analytics.totalAnalyses")} value={data.total_analyses} icon={BarChart3}
          trend={data.week_change} delay={0} />
        <StatCard label={t("analytics.avgConfidence")} value={`${data.avg_confidence}%`} icon={Shield} delay={0.05} />
        <StatCard label={t("analytics.thisWeek")} value={data.this_week} sub={`${t("analytics.lastWeek")}: ${data.last_week}`}
          icon={TrendingUp} delay={0.1} />
        <StatCard label={t("analytics.quota")} value={`${data.usage_count}/${data.usage_limit}`}
          sub={`${PLAN_LABELS[data.plan] || data.plan} · ${data.credits_balance} ${t("analytics.credits")}`}
          icon={Zap} delay={0.15} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Weekly Trend Chart */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="lg:col-span-2 rounded-md border border-border bg-card p-5" data-testid="weekly-trend-chart">
          <h2 className="text-sm font-mono uppercase tracking-widest text-muted-foreground mb-4">{t("analytics.weeklyTrend")}</h2>
          {weeklyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="count" name={t("analytics.analyses")} stroke="hsl(var(--brand-primary))"
                  strokeWidth={2} dot={{ r: 4, fill: "hsl(var(--brand-primary))" }} activeDot={{ r: 6 }} />
                <Line type="monotone" dataKey="avg_confidence" name={t("analytics.confidence")} stroke="hsl(142, 71%, 45%)"
                  strokeWidth={2} strokeDasharray="5 5" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[240px] text-sm text-muted-foreground">
              {t("analytics.noData")}
            </div>
          )}
        </motion.div>

        {/* Verdict Distribution */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="rounded-md border border-border bg-card p-5" data-testid="verdict-chart">
          <h2 className="text-sm font-mono uppercase tracking-widest text-muted-foreground mb-4">{t("analytics.verdictDist")}</h2>
          {pieData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75}
                    dataKey="value" paddingAngle={3} strokeWidth={0}>
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-3 mt-3 justify-center">
                {pieData.map(d => (
                  <div key={d.name} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                    <span className="text-xs font-mono text-muted-foreground">{d.name}: {d.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-[180px] text-sm text-muted-foreground">
              {t("analytics.noData")}
            </div>
          )}
        </motion.div>
      </div>

      {/* Row 3: Confidence by Verdict + Media Type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Confidence by Verdict */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }}
          className="rounded-md border border-border bg-card p-5" data-testid="confidence-by-verdict-chart">
          <h2 className="text-sm font-mono uppercase tracking-widest text-muted-foreground mb-4">{t("analytics.confByVerdict")}</h2>
          {Object.keys(data.confidence_by_verdict || {}).length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={Object.entries(data.confidence_by_verdict || {}).map(([v, d]) => ({
                  verdict: v, avg: d.avg, min: d.min, max: d.max, fill: VERDICT_COLORS[v] || "#888",
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="verdict" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" domain={[0, 100]} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="avg" name="Avg %" radius={[4, 4, 0, 0]}>
                    {Object.entries(data.confidence_by_verdict || {}).map(([v], i) => (
                      <Cell key={i} fill={VERDICT_COLORS[v] || "#888"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-4 mt-3">
                {Object.entries(data.confidence_by_verdict || {}).map(([v, d]) => (
                  <div key={v} className="text-xs font-mono">
                    <span style={{ color: VERDICT_COLORS[v] || "#888" }} className="font-semibold">{v}</span>
                    <span className="text-muted-foreground ml-1">min {d.min}% / avg {d.avg}% / max {d.max}%</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">{t("analytics.noData")}</div>
          )}
        </motion.div>

        {/* Media Type Distribution */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32 }}
          className="rounded-md border border-border bg-card p-5" data-testid="media-type-chart">
          <h2 className="text-sm font-mono uppercase tracking-widest text-muted-foreground mb-4">{t("analytics.mediaType")}</h2>
          {Object.keys(data.media_type_distribution || {}).length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={Object.entries(data.media_type_distribution || {}).map(([name, value]) => ({
                      name: name === "upload" ? "Upload" : name === "url" ? "URL" : name,
                      value,
                      color: MEDIA_COLORS[name] || "#6b7280",
                    }))}
                    cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                    dataKey="value" paddingAngle={3} strokeWidth={0}
                  >
                    {Object.entries(data.media_type_distribution || {}).map(([name], i) => (
                      <Cell key={i} fill={MEDIA_COLORS[name] || "#6b7280"} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-3 mt-3 justify-center">
                {Object.entries(data.media_type_distribution || {}).map(([name, value]) => (
                  <div key={name} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: MEDIA_COLORS[name] || "#6b7280" }} />
                    <span className="text-xs font-mono text-muted-foreground">{name === "upload" ? "Upload" : name === "url" ? "URL" : name}: {value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">{t("analytics.noData")}</div>
          )}
        </motion.div>
      </div>

      {/* Usage Progress + Recent Analyses */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage Bar */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="rounded-md border border-border bg-card p-5" data-testid="usage-progress">
          <h2 className="text-sm font-mono uppercase tracking-widest text-muted-foreground mb-4">{t("analytics.usageProgress")}</h2>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between mb-1.5">
                <span className="text-xs text-muted-foreground">{t("analytics.monthlyUsage")}</span>
                <span className="text-xs font-mono font-semibold">{usagePct}%</span>
              </div>
              <div className="w-full h-3 rounded-full bg-secondary">
                <div className={`h-full rounded-full transition-all ${
                  usagePct >= 100 ? "bg-red-500" : usagePct >= 80 ? "bg-amber-500" : "bg-brand-primary"
                }`} style={{ width: `${usagePct}%` }} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">{data.usage_count} / {data.usage_limit} {t("analytics.analyses")}</p>
            </div>
            <div className="pt-3 border-t border-border">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{t("analytics.creditsBalance")}</span>
                <span className="text-sm font-bold text-brand-primary">{data.credits_balance}</span>
              </div>
            </div>
            <div className="pt-2">
              <Link to="/pricing" className="text-xs text-brand-primary hover:underline flex items-center gap-1">
                {t("analytics.upgradePlan")} <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Recent Analyses */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
          className="rounded-md border border-border bg-card p-5" data-testid="recent-analyses">
          <h2 className="text-sm font-mono uppercase tracking-widest text-muted-foreground mb-4">{t("analytics.recentAnalyses")}</h2>
          {(data.recent_analyses || []).length > 0 ? (
            <div className="space-y-2">
              {data.recent_analyses.map((a, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div className="flex-1 min-w-0 pr-3">
                    <p className="text-sm truncate">{a.video_name}</p>
                    <p className="text-[10px] font-mono text-muted-foreground">
                      {a.created_at ? new Date(a.created_at).toLocaleDateString("id-ID") : "-"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-mono font-bold px-1.5 py-0.5 rounded-sm ${
                      a.verdict === "REAL" ? "bg-verdict-real/10 text-verdict-real" :
                      a.verdict === "FAKE" ? "bg-red-500/10 text-red-400" :
                      "bg-amber-500/10 text-amber-400"
                    }`}>{a.verdict}</span>
                    <span className="text-xs font-mono text-muted-foreground">{Math.round(a.confidence)}%</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              <Clock className="w-4 h-4 mr-2" /> {t("analytics.noAnalyses")}
            </div>
          )}
          <div className="mt-3 pt-2 border-t border-border">
            <Link to="/history" className="text-xs text-brand-primary hover:underline flex items-center gap-1">
              {t("analytics.viewAll")} <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
